import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type {
  CreateSubcontractAssignmentRequest,
  RateSubcontractAssignmentRequest,
  SubcontractAssignment,
  SubcontractResolution,
  UpdateSubcontractAssignmentRequest,
} from '../types/service.types';

/**
 * D18-01 — the Sublet pipeline client. Quote and invoice are multipart (an
 * optional document); every other verb is JSON. The explicit multipart header
 * is required: the axios instance's JSON default would otherwise serialise
 * FormData as JSON (the Phase-17 IN-03 lesson).
 */
/** The list endpoint's `per_page` ceiling; the default page is 25 (D17-29 / IN-02 precedent). */
export const SUBCONTRACT_LIST_PER_PAGE = 100;

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const verbUrl = (id: string, verb: string) => `/subcontract-assignments/${id}/${verb}`;

export const subcontractAssignmentApi = {
  list: (workOrderId: string) =>
    tenantApi.getList<SubcontractAssignment>(`/work-orders/${workOrderId}/subcontract-assignments`, {
      params: { per_page: SUBCONTRACT_LIST_PER_PAGE },
    }),

  request: (workOrderId: string, data: CreateSubcontractAssignmentRequest) =>
    tenantApi.postOne<SubcontractAssignment>(
      `/work-orders/${workOrderId}/subcontract-assignments`,
      data,
    ),

  update: (id: string, data: UpdateSubcontractAssignmentRequest) =>
    tenantApi.patchOne<SubcontractAssignment>(`/subcontract-assignments/${id}`, data),

  quote: (id: string, formData: FormData) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'quote'), formData, MULTIPART),

  invoice: (id: string, formData: FormData) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'invoice'), formData, MULTIPART),

  approve: (id: string, customerSignatureId: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'approve'), {
      customer_signature_id: customerSignatureId,
    }),

  dispatch: (id: string, promisedReturnDate: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'dispatch'), {
      promised_return_date: promisedReturnDate,
    }),

  start: (id: string) => tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'start')),

  complete: (id: string) => tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'complete')),

  settle: (id: string) => tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'settle')),

  dispute: (id: string, reason: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'dispute'), { reason }),

  // D19-20 — JSON body (rating 1-5 + optional notes); no multipart.
  rate: (id: string, body: RateSubcontractAssignmentRequest) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'rate'), body),

  cancel: (id: string, reason: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'cancel'), { reason }),

  decline: (id: string, reason: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'decline'), { reason }),

  voidAssignment: (id: string, reason: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'void'), { reason }),

  resolve: (id: string, resolution: SubcontractResolution, reason?: string) =>
    tenantApi.postOne<SubcontractAssignment>(verbUrl(id, 'resolve'), {
      resolution,
      ...(reason !== undefined ? { reason } : {}),
    }),
};
